// frontend/src/offers/useOffer.js
// The ONLY two hooks the rest of the site needs:
//   useActiveOffer()     -> { offer, serverOffsetMs }   (offer is null when none is live)
//   useOfferQuote(items) -> discount / total / freeQtyFor(line) / nudges for a cart
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { fetchActiveOffer } from "../../redux/slices/offerSlice";
import { buildQuote } from "./offerEngine";

const MAX_TIMEOUT = 2147483000; // setTimeout limit (~24.8 days)

export function useActiveOffer() {
  const dispatch = useDispatch();
  const store = useStore();
  const { offer, serverOffsetMs, loaded, loading } = useSelector((s) => s.offer);
  const [tick, setTick] = useState(0);

  // first load (the bar in the navbar keeps it fresh afterwards)
  // Several components (navbar bar, page, grid) call this hook in the same render pass. Each of them
  // would see loaded=false/loading=false from its own snapshot and send its own identical request,
  // so we check the LIVE store right before dispatching: only the first one actually fires.
  useEffect(() => {
    const cur = store.getState().offer;
    if (!cur.loaded && !cur.loading) dispatch(fetchActiveOffer());
  }, [loaded, loading, dispatch, store]);

  // flip exactly when the offer starts / ends, without polling
  useEffect(() => {
    if (!offer) return undefined;
    const now = Date.now() + serverOffsetMs;
    const next = [Date.parse(offer.startsAt), Date.parse(offer.endsAt)]
      .filter((t) => Number.isFinite(t) && t > now)
      .sort((a, b) => a - b)[0];
    if (!next) return undefined;
    const id = setTimeout(() => setTick((n) => n + 1), Math.min(next - now + 50, MAX_TIMEOUT));
    return () => clearTimeout(id);
  }, [offer, serverOffsetMs, tick]);

  const now = Date.now() + serverOffsetMs;
  const live =
    offer && Date.parse(offer.startsAt) <= now && now < Date.parse(offer.endsAt) ? offer : null;
  return { offer: live, serverOffsetMs };
}

export function useOfferQuote(cartProducts) {
  const { offer } = useActiveOffer();
  return useMemo(() => buildQuote(cartProducts, offer), [cartProducts, offer]);
}
